import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import ExtensionsPage from './pages/extensions/ExtensionsPage';
import ExtensionFormPage from './pages/extensions/ExtensionFormPage';
import RingGroupsPage from './pages/ring-groups/RingGroupsPage';
import RingGroupFormPage from './pages/ring-groups/RingGroupFormPage';
import IVRPage from './pages/ivr/IVRPage';
import IVRFormPage from './pages/ivr/IVRFormPage';
import VoicemailPage from './pages/voicemail/VoicemailPage';
import CallHistoryPage from './pages/call-history/CallHistoryPage';
import TimeConditionsPage from './pages/time-conditions/TimeConditionsPage';
import SettingsPage from './pages/settings/SettingsPage';
import UsersPage from './pages/users/UsersPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Extensions */}
        <Route path="extensions" element={<ExtensionsPage />} />
        <Route path="extensions/new" element={<ExtensionFormPage />} />
        <Route path="extensions/:id/edit" element={<ExtensionFormPage />} />
        
        {/* Ring Groups */}
        <Route path="ring-groups" element={<RingGroupsPage />} />
        <Route path="ring-groups/new" element={<RingGroupFormPage />} />
        <Route path="ring-groups/:id/edit" element={<RingGroupFormPage />} />
        
        {/* IVR / Auto-Attendant */}
        <Route path="ivr" element={<IVRPage />} />
        <Route path="ivr/new" element={<IVRFormPage />} />
        <Route path="ivr/:id/edit" element={<IVRFormPage />} />
        
        {/* Voicemail */}
        <Route path="voicemail" element={<VoicemailPage />} />
        
        {/* Call History */}
        <Route path="call-history" element={<CallHistoryPage />} />
        
        {/* Time Conditions */}
        <Route path="time-conditions" element={<TimeConditionsPage />} />
        
        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />
        
        {/* User Management (Admin only) */}
        <Route path="users" element={<UsersPage />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
